/* eslint-disable no-redeclare */
import { z } from 'zod';

import {
  EstimatedSpendingDto,
  ImmediateCoverageDto,
  KindOfMedicarePlanDto,
} from '../../../dtos';

export const JourneyIdDto = z.enum([
  'health-default',
  'medicare-advantage',
  'medicare-supplement',
  'medicare-generic',
  'life-default',
  '114',
  '112',
]);
export type JourneyIdDto = z.infer<typeof JourneyIdDto>;

export const GetJourneyRequestDto = z.object({
  journeyId: JourneyIdDto,
  env: z.enum(['production', 'development']),
});

export type GetJourneyRequestDto = z.infer<typeof GetJourneyRequestDto>;

export const MedicareAdvantagePlateDto = z.enum([
  'medicare_coverage_urgency.advantage',
  'fname-lname-email-location-phone.advantage',
  'gender-dob-dob_category.advantage',
  'additional_coverage.advantage',
  'zip_code.advantage',
  'redirect_typ_medicare_advantage',
]);
export type MedicareAdvantagePlateDto = z.infer<
  typeof MedicareAdvantagePlateDto
>;

export const MedicareSupplementPlateDto = z.enum([
  'medicare_coverage_urgency',
  'fname-lname-email-location-phone',
  'gender-dob-dob_category',
  'gender.supplement',
  'dob-dob_category.supplement',
  'additional_coverage',
  'zip_code',
  'redirect_typ_medicare',
  'visit_purpose',
  'first_time_enrollment.medicare',
]);
export type MedicareSupplementPlateDto = z.infer<
  typeof MedicareSupplementPlateDto
>;

export const MedicareGenericPlateDto = z.enum([
  'kind_of_medicare_plan',
  'medicare_coverage_urgency.advantage',
  'zip_code',
  'gender-dob-dob_category.skip.with_tooltip',
  'fname-lname-email-location-phone.advantage',
  'redirect_typ_medicare',
  'redirect_quotes_healthcare_com',
  'redirect_med_advantage',
  'redirect_medicare',
]);
export type MedicareGenericPlateDto = z.infer<typeof MedicareGenericPlateDto>;

export const HealthPlateDto = z.enum([
  'redirect_aca_healthcare_com',
  'gender-dob-dob_category.health',
  'coverage_duration',
  'change_pharmacy.health',
  'estimated_spending',
  'zip_code.health',
  'household_size',
  'household_income',
  'household_size-household_income',
  'redirect_typ_health',
  'income_subsidy',
  'quality_life_event',
  'redirect_stm_healthcare_com',
  'insure_who',
  'redirect_medicare_healthcare_com',
  'spouse_children',
  'immediate_coverage',
  'fname-lname-email-location-phone.health',
  'tobacco',
  'senior_check.health',
  'first_time_enrollment.health',
]);
export type HealthPlateDto = z.infer<typeof HealthPlateDto>;

export const LifeInsurancePlateDto = z.enum([
  'tobacco',
  'insurance_options',
  'employment_status',
  'annual_income',
  'health_assessment',
  'height_weight',
  'medical_history',
  'family_medical_history',
  'redirect_typ_life',
  'gender-dob-dob_category',
  'employment_status',
  'fname-lname-email-location-phone',
]);
export type LifeInsurancePlateDto = z.infer<typeof LifeInsurancePlateDto>;

export const CoverageDurationDto = z.enum([
  'permanent',
  'temporary',
  'not_sure',
]);

export type CoverageDurationDto = z.infer<typeof CoverageDurationDto>;

export const SeniorCheckDto = z.enum(['u65', 'o65']);
export type SeniorCheckDto = z.infer<typeof SeniorCheckDto>;

export const SharedPlateDto = z.enum(['redirect_quotes']);
export type SharedPlateDto = z.infer<typeof SharedPlateDto>;

export const AdditionalCoverageDto = z.enum([
  'advantage',
  'supplement',
  'none',
]);
export type AdditionalCoverageDto = z.infer<typeof AdditionalCoverageDto>;

export const InsureWhoDto = z.enum(['just_me', 'myself_and_others']);

export type InsureWhoDto = z.infer<typeof InsureWhoDto>;

export const AskForZipCodeDto = z.enum(['true', 'false']);
export type AskForZipCodeDto = z.infer<typeof AskForZipCodeDto>;

export const ChangePharmacyDto = z.enum(['no', 'yes']);
export type ChangePharmacyDto = z.infer<typeof ChangePharmacyDto>;

export const ChannelDto = z.enum(['online', 'online_and_in_person']);
export type ChannelDto = z.infer<typeof ChannelDto>;

export const DobCategoryDto = z.enum(['u65', 'o65']);
export type DobCategoryDto = z.infer<typeof DobCategoryDto>;

export const ForWhoHelpDto = z.enum(['self', 'couple', 'children']);
export type ForWhoHelpDto = z.infer<typeof ForWhoHelpDto>;

export const GenderDto = z.enum(['m', 'f']);
export type GenderDto = z.infer<typeof GenderDto>;

export const HasMedicareDto = z.enum(['no', 'yes']);
export type HasMedicareDto = z.infer<typeof HasMedicareDto>;

export const HouseholdIncomeDto = z.enum(['below', 'between', 'over']);
export type HouseholdIncomeDto = z.infer<typeof HouseholdIncomeDto>;

export const HouseholdSizeDto = z.enum(['1', '2', '3', '4', '5', '6']);
export type HouseholdSizeDto = z.infer<typeof HouseholdSizeDto>;

export const InsuranceTypeDto = z.enum([
  'aca',
  'stm_and_alternatives',
  'not_sure',
]);
export type InsuranceTypeDto = z.infer<typeof InsuranceTypeDto>;

export const IsOepDto = z.enum(['is-oep-yes', 'is-oep-no']);
export type IsOepDto = z.infer<typeof IsOepDto>;

export const IsPhoenixDto = z.enum(['true', 'false']);
export type IsPhoenixDto = z.infer<typeof IsPhoenixDto>;

export const KindOfCoverageDto = z.enum(['a_b', 'a', 'none']);
export type KindOfCoverageDto = z.infer<typeof KindOfCoverageDto>;

export const MedicareCoverageUrgencyDto = z.enum([
  'right_away',
  'next_month_or_later',
  'not_sure',
]);
export type MedicareCoverageUrgencyDto = z.infer<
  typeof MedicareCoverageUrgencyDto
>;

export const QualityLifeEventDto = z.enum([
  'lost_coverage',
  'got_new_job',
  'had_a_baby',
  'moved_new_state',
  'none_of_these',
]);
export type QualityLifeEventDto = z.infer<typeof QualityLifeEventDto>;

export const TimeOfOccurrenceDto = z.enum([
  'last_60_days',
  'next_60_days',
  'after_60_days',
]);
export type TimeOfOccurrenceDto = z.infer<typeof TimeOfOccurrenceDto>;

export const TobaccoDto = z.enum(['n', 'y']);
export type TobaccoDto = z.infer<typeof TobaccoDto>;

export const TypeOfHelpDto = z.enum([
  'therapy',
  'medication',
  'therapy_and_medication',
  'i_dont_know',
]);
export type TypeOfHelpDto = z.infer<typeof TypeOfHelpDto>;

export const TypeOfPaymentDto = z.enum(['insurance', 'cash']);
export type TypeOfPaymentDto = z.infer<typeof TypeOfPaymentDto>;

export const QuestionsDto = z.object({
  additional_coverage: AdditionalCoverageDto,
  ask_for_zip_code: AskForZipCodeDto,
  change_pharmacy: ChangePharmacyDto,
  channel: ChannelDto,
  coverage_duration: CoverageDurationDto,
  dob_category: DobCategoryDto,
  estimated_spending: EstimatedSpendingDto,
  for_who_help: ForWhoHelpDto,
  gender: GenderDto,
  has_medicare: HasMedicareDto,
  household_income: HouseholdIncomeDto,
  household_size: HouseholdSizeDto,
  immediate_coverage: ImmediateCoverageDto,
  insurance_type: InsuranceTypeDto,
  insure_who: InsureWhoDto,
  is_oep: IsOepDto,
  is_phoenix: IsPhoenixDto,
  kind_of_coverage: KindOfCoverageDto,
  kind_of_medicare_plan: KindOfMedicarePlanDto,
  medicare_coverage_urgency: MedicareCoverageUrgencyDto,
  quality_life_event: QualityLifeEventDto,
  senior_check: SeniorCheckDto,
  time_of_occurrence: TimeOfOccurrenceDto,
  tobacco: TobaccoDto,
  type_of_help: TypeOfHelpDto,
  type_of_payment: TypeOfPaymentDto,
});
export type QuestionsDto = z.infer<typeof QuestionsDto>;

export const QuestionDto = QuestionsDto.keyof();
export type QuestionDto = z.infer<typeof QuestionDto>;

export const PlateNameDto = MedicareAdvantagePlateDto.or(
  MedicareSupplementPlateDto,
)
  .or(MedicareGenericPlateDto)
  .or(LifeInsurancePlateDto)
  .or(HealthPlateDto)
  .or(SharedPlateDto);

export type PlateNameDto = z.infer<typeof PlateNameDto>;

export const TransitionDto = z.object({
  plate: PlateNameDto,
  questions: QuestionsDto.partial(),
  validate: z.boolean(),
});

export type TransitionDto = z.infer<typeof TransitionDto>;

const PlateDto = z
  .object({
    id: PlateNameDto,
    transitions: TransitionDto.array(),
  })
  .partial();

export const GetJourneyResponseDto = z.object({
  id: z.string(),
  plates: z.record(PlateNameDto, PlateDto),
  start: PlateNameDto,
});

export type GetJourneyResponseDto = z.infer<typeof GetJourneyResponseDto>;
